// ✅ src/pages/VehicleV2/ProgressMaster.jsx
import React, {
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
} from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";

import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { FaSearch, FaPrint, FaArrowLeft, FaInfoCircle } from "react-icons/fa";

const API_BASE = "https://nissi-india.com/api/progressMaster";

/* ---------------- helpers ---------------- */
const toStr = (v) => String(v ?? "").trim();

const isSelect = (v) => {
  const s = String(v ?? "").trim().toLowerCase();
  return !s || s === "select" || s === "selec" || s === "0" || s === "null";
};

const pad2 = (n) => String(n).padStart(2, "0");

const fmtDate = (isoOrDate) => {
  if (!isoOrDate) return "";
  if (typeof isoOrDate === "string" && isoOrDate.includes("/")) return isoOrDate;
  const d = new Date(isoOrDate);
  if (Number.isNaN(d.getTime())) return String(isoOrDate);
  return `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()}`;
};

const fmtNum2 = (v) => {
  if (v === null || v === undefined || v === "") return "";
  const n = Number(String(v).replace(/,/g, ""));
  if (!Number.isFinite(n)) return String(v);
  return n.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const years2006ToNow = () => {
  const now = new Date().getFullYear();
  const arr = [];
  for (let y = 2006; y <= now; y++) arr.push(String(y));
  return arr;
};

const monthOptions = [
  { label: "Select", value: "" },
  { label: "January", value: "January" },
  { label: "February", value: "February" },
  { label: "March", value: "March" },
  { label: "April", value: "April" },
  { label: "May", value: "May" },
  { label: "June", value: "June" },
  { label: "July", value: "July" },
  { label: "August", value: "August" },
  { label: "September", value: "September" },
  { label: "October", value: "October" },
  { label: "November", value: "November" },
  { label: "December", value: "December" },
];

function normalizeRow(x, idx) {
  return {
    sno: idx + 1,
    fld_vehicle_code: x?.fld_vehicle_code ?? x?.vehicleCode ?? "",
    fld_vehicle_name: x?.fld_vehicle_name ?? x?.vehicleName ?? "",
    fld_vehicle_regno: x?.fld_vehicle_regno ?? x?.regNo ?? "",
    fld_hsdfillingWorkinhr: fmtNum2(x?.fld_hsdfillingWorkinhr ?? x?.workHr),
    fld_hsdfillinghr: fmtNum2(x?.fld_hsdfillinghr ?? x?.hsdHr),
    fld_hsdfillingWorkingKM: fmtNum2(x?.fld_hsdfillingWorkingKM ?? x?.workKm),
    fld_hsdfillingkm: fmtNum2(x?.fld_hsdfillingkm ?? x?.hsdKm),
    fld_diselconsumptionhr: fmtNum2(x?.fld_diselconsumptionhr ?? x?.consHr),
    fld_diselconsumptionkm: fmtNum2(x?.fld_diselconsumptionkm ?? x?.consKm),
    cumulativehrs: fmtNum2(x?.cumulativehrs ?? x?.cumHr),
    cumulativekm: fmtNum2(x?.cumulativekm ?? x?.cumKm),
    fld_progressentrydate: fmtDate(x?.fld_progressentrydate ?? x?.lastUpdatedDate),
  };
}

export default function ProgressMaster() {
  const navigate = useNavigate();
  const { isDark, skin, setSkin } = useContext(ThemeContext);

  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  /* ---------------- localStorage session values ---------------- */
  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const sessionBranchId = localStorage.getItem("branchId") || "";
  const sessionBranchType = localStorage.getItem("branchType") || "";
  const userId = localStorage.getItem("userId") || "";

  const apiHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": sessionBranchId,
      "x-branchtype": sessionBranchType,
      "x-userid": userId,
    }),
    [finYearId, finYear, sessionBranchId, sessionBranchType, userId]
  );

  /* ---------------- state ---------------- */
  const [vehicleMode, setVehicleMode] = useState("O");
  const [branches, setBranches] = useState([{ label: "Select", value: "" }]);
  const [branch, setBranch] = useState("");
  const [vehicleSubTypes, setVehicleSubTypes] = useState([{ label: "Select", value: "" }]);
  const [vehicleSubType, setVehicleSubType] = useState("");

  const [vehicleCode, setVehicleCode] = useState("");
  const [vehicleName, setVehicleName] = useState("");
  const [regNo, setRegNo] = useState("");

  const [month, setMonth] = useState("");
  const [year, setYear] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const [codeSug, setCodeSug] = useState([]);
  const [nameSug, setNameSug] = useState([]);
  const [regSug, setRegSug] = useState([]);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);

  const initLoadedRef = useRef(false);

  const ownHire = vehicleMode === "H" ? "HIRE" : "OWN";
  const prvType = vehicleMode === "H" ? "H" : "O";

  /* ---------------- init ---------------- */
  const loadInit = useCallback(async () => {
    try {
      setPageLoading(true);

      const res = await axios.get(`${API_BASE}/init`, {
        headers: apiHeaders,
        params: { ownHire },
      });

      const data = res?.data || {};
      const b = Array.isArray(data?.branches) ? data.branches : [];
      const vst = Array.isArray(data?.vehicleSubTypes) ? data.vehicleSubTypes : [];
      const defaults = data?.defaults || {};

      setBranches(b.length ? b : [{ label: "Select", value: "" }]);
      setVehicleSubTypes(vst.length ? vst : [{ label: "Select", value: "" }]);

      setBranch(
        toStr(
          defaults?.branchId ||
            ((sessionBranchType || "").toUpperCase() !== "HO" ? sessionBranchId : "")
        )
      );
      setMonth(toStr(defaults?.month));
      setYear(toStr(defaults?.year));
      setFromDate(toStr(defaults?.fromDate));
      setToDate(toStr(defaults?.toDate));
    } catch (e) {
      toast.error(e?.response?.data?.message || e.message || "Failed to load initial data");
    } finally {
      setPageLoading(false);
    }
  }, [apiHeaders, ownHire, sessionBranchId, sessionBranchType]);

  useEffect(() => {
    if (!userId) {
      navigate("/login");
      return;
    }
    loadInit();
  }, [loadInit, navigate, userId]);

  useEffect(() => {
    if (pageLoading) return;
    if (initLoadedRef.current) return;
    initLoadedRef.current = true;
    handleSearch(true);
  }, [pageLoading]);

  useEffect(() => {
    if (!initLoadedRef.current) return;
    loadInit();
  }, [vehicleMode, loadInit]);

  /* ---------------- autocomplete ---------------- */
  const fetchAuto = useCallback(
    async (type, text) => {
      const q = toStr(text);
      if (!q) return [];

      const url =
        type === "code"
          ? `${API_BASE}/suggest/vehiclecode`
          : type === "name"
          ? `${API_BASE}/suggest/vehiclename`
          : `${API_BASE}/suggest/vehicleregno`;

      const res = await axios.get(url, {
        headers: apiHeaders,
        params: {
          prefix: q,
          branchid: branch || sessionBranchId || "",
          prvehtype: prvType,
          vehcode: type === "code" ? "%" : vehicleCode || "%",
          vehiclename: type === "name" ? "%" : vehicleName || "%",
          vehregno: type === "regno" ? "%" : regNo || "%",
        },
      });

      return Array.isArray(res?.data?.items) ? res.data.items : [];
    },
    [apiHeaders, branch, sessionBranchId, prvType, vehicleCode, vehicleName, regNo]
  );

  const onTypeVehicleCode = async (v) => {
    setVehicleCode(v);
    try {
      const items = await fetchAuto("code", v);
      setCodeSug(items);
    } catch {
      setCodeSug([]);
    }
  };

  const onTypeVehicleName = async (v) => {
    setVehicleName(v);
    try {
      const items = await fetchAuto("name", v);
      setNameSug(items);
    } catch {
      setNameSug([]);
    }
  };

  const onTypeRegNo = async (v) => {
    setRegNo(v);
    try {
      const items = await fetchAuto("regno", v);
      setRegSug(items);
    } catch {
      setRegSug([]);
    }
  };

  /* ---------------- params ---------------- */
  const buildListParams = useCallback(() => {
    return {
      prvehtype: prvType,
      branchid: isSelect(branch) ? "" : branch,
      vehcode: vehicleCode || "",
      vehiclename: vehicleName || "",
      vehregno: regNo || "",
      month: month || "Select",
      year: year || "Select",
      pageIndex: 0,
      pageSize: 500,
    };
  }, [prvType, branch, vehicleCode, vehicleName, regNo, month, year]);

  /* ---------------- actions ---------------- */
  async function handleSearch(silent = false) {
    setLoading(true);
    try {
      const params = buildListParams();

      const res = await axios.get(`${API_BASE}/list`, {
        headers: apiHeaders,
        params,
      });

      if (res?.data?.success === false) {
        toast.error(res?.data?.message || "Search failed");
        setRows([]);
        return;
      }

      const list = Array.isArray(res?.data?.rows) ? res.data.rows : [];
      setRows(list);

      if (!silent) {
        toast.success(`Loaded ${list.length} row(s)`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || e.message || "Search failed");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }

  const handleClear = () => {
    setVehicleCode("");
    setVehicleName("");
    setRegNo("");
    setVehicleSubType("");
    setMonth("");
    setYear("");
    setFromDate("");
    setToDate("");
    setCodeSug([]);
    setNameSug([]);
    setRegSug([]);
    setBranch((sessionBranchType || "").toUpperCase() !== "HO" ? sessionBranchId : "");
    setRows([]);
  };

  const handlePrint = async () => {
    try {
      const res = await axios.get(`${API_BASE}/printUrl`, {
        headers: apiHeaders,
        params: {
          ownHire,
          branchId: isSelect(branch) ? "" : branch,
          vehicleCode,
          vehicleName,
          vehicleRegno: regNo,
          month,
          year,
          fromDate,
          toDate,
        },
      });

      if (res?.data?.success === false) {
        toast.error(res?.data?.message || "Print URL generation failed");
        return;
      }

      const url = res?.data?.url || res?.data?.legacyUrl;
      if (!url) {
        toast.error("Print URL not available");
        return;
      }

      window.open(url, "_blank");
    } catch (e) {
      toast.error(e?.response?.data?.message || e.message || "Print failed");
    }
  };

  const openVehicleDetail = async (vehCode) => {
    try {
      const res = await axios.get(`${API_BASE}/openVehicleHsdReport`, {
        headers: apiHeaders,
        params: {
          vehicleCode: vehCode,
          month: month || "Select",
          year: year || "Select",
          fromDate: fromDate || "",
          toDate: toDate || "",
        },
      });

      if (res?.data?.success === false) {
        toast.error(res?.data?.message || "Open detail failed");
        return;
      }

      const url = res?.data?.url || res?.data?.legacyUrl;
      if (!url) {
        toast.error("Detail URL not available");
        return;
      }

      window.open(url, "_blank");
    } catch (e) {
      toast.error(e?.response?.data?.message || e.message || "Open detail failed");
    }
  };

  const normalized = useMemo(() => rows.map(normalizeRow), [rows]);

  /* ---------------- styles ---------------- */
  const inputClass = `w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
    isDark
      ? "bg-gray-700/30 border-gray-600 text-white"
      : "bg-white border-gray-300 text-gray-900"
  }`;

  const labelClass = `block text-sm font-medium mb-2 ${
    isDark ? "text-gray-300" : "text-gray-700"
  }`;

  return (
    <VehicleLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">PROGRESS MASTER</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4 mb-5">
            <div className="flex items-center gap-4 flex-wrap">
              <div>
                <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                  Progress Master
                </h1>
                <div
                  className={`text-sm flex items-center gap-2 mt-1 ${
                    isDark ? "text-gray-300" : "text-gray-600"
                  }`}
                >
                  <FaInfoCircle />
                  Progress report with vehicle HSD / KM / HR summary
                </div>
              </div>

              <span
                className={`px-3 py-1 rounded-full text-sm font-medium ${
                  isDark ? "bg-blue-900 text-blue-100" : "bg-blue-100 text-blue-800"
                }`}
              >
                {normalized.length} Records
              </span>

              {(loading || pageLoading) && (
                <span className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                  Loading...
                </span>
              )}
            </div>

            <div className="flex items-center gap-3 flex-wrap">
              <button
                onClick={() => handleSearch(false)}
                disabled={loading || pageLoading}
                className={`flex items-center gap-2 ${buttonClass} ${hoverClass} text-white px-5 py-2 rounded-lg font-semibold shadow-md disabled:opacity-60`}
              >
                <FaSearch className="w-4 h-4" />
                {loading ? "Searching..." : "Search"}
              </button>

              <button
                onClick={handlePrint}
                disabled={loading || pageLoading}
                className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-5 py-2 rounded-lg font-semibold shadow-md disabled:opacity-60"
              >
                <FaPrint className="w-4 h-4" />
                Print
              </button>

              <button
                onClick={handleClear}
                disabled={loading || pageLoading}
                className="flex items-center gap-2 bg-yellow-500 hover:bg-yellow-600 text-white px-5 py-2 rounded-lg font-semibold shadow-md disabled:opacity-60"
              >
                Clear
              </button>

              <button
                onClick={() => navigate(-1)}
                className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                  isDark
                    ? "border-gray-600 text-white hover:bg-gray-800"
                    : "border-gray-300 text-gray-700 hover:bg-gray-50"
                }`}
              >
                <FaArrowLeft className="w-4 h-4" />
                Back
              </button>
            </div>
          </div>

          <div
            className={`rounded-xl p-6 mb-6 border ${
              isDark ? "bg-gray-800 border-gray-700" : "bg-gray-50 border-gray-200"
            }`}
          >
            <div className="flex items-center gap-6 mb-6 flex-wrap">
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name="vehicleMode"
                  checked={vehicleMode === "O"}
                  onChange={() => setVehicleMode("O")}
                />
                <span>Own Vehicle</span>
              </label>

              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name="vehicleMode"
                  checked={vehicleMode === "H"}
                  onChange={() => setVehicleMode("H")}
                />
                <span>Hire Vehicle</span>
              </label>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div>
                <label className={labelClass}>Branch</label>
                <select
                  className={inputClass}
                  value={branch}
                  onChange={(e) => setBranch(e.target.value)}
                  disabled={(sessionBranchType || "").toUpperCase() !== "HO"}
                >
                  {branches.map((b, i) => (
                    <option key={`${b.value}-${i}`} value={b.value}>
                      {b.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className={labelClass}>Vehicle Code</label>
                <input
                  className={inputClass}
                  value={vehicleCode}
                  onChange={(e) => onTypeVehicleCode(e.target.value)}
                  list="vehCodeSug"
                  placeholder="Type vehicle code..."
                />
                <datalist id="vehCodeSug">
                  {codeSug.map((x, i) => (
                    <option key={i} value={x} />
                  ))}
                </datalist>
              </div>

              <div>
                <label className={labelClass}>Vehicle Registration No</label>
                <input
                  className={inputClass}
                  value={regNo}
                  onChange={(e) => onTypeRegNo(e.target.value)}
                  list="vehRegSug"
                  placeholder="Type registration no..."
                />
                <datalist id="vehRegSug">
                  {regSug.map((x, i) => (
                    <option key={i} value={x} />
                  ))}
                </datalist>
              </div>

              <div>
                <label className={labelClass}>Vehicle Name</label>
                <input
                  className={inputClass}
                  value={vehicleName}
                  onChange={(e) => onTypeVehicleName(e.target.value)}
                  list="vehNameSug"
                  placeholder="Type vehicle name..."
                />
                <datalist id="vehNameSug">
                  {nameSug.map((x, i) => (
                    <option key={i} value={x} />
                  ))}
                </datalist>
              </div>

              <div>
                <label className={labelClass}>Vehicle Sub Type</label>
                <select
                  className={inputClass}
                  value={vehicleSubType}
                  onChange={(e) => setVehicleSubType(e.target.value)}
                >
                  {vehicleSubTypes.map((v, i) => (
                    <option key={`${v.value}-${i}`} value={v.value}>
                      {v.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className={labelClass}>Month</label>
                <select
                  className={inputClass}
                  value={month}
                  onChange={(e) => setMonth(e.target.value)}
                >
                  {monthOptions.map((m) => (
                    <option key={m.label} value={m.value}>
                      {m.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className={labelClass}>Year</label>
                <select
                  className={inputClass}
                  value={year}
                  onChange={(e) => setYear(e.target.value)}
                >
                  <option value="">Select</option>
                  {years2006ToNow().map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className={labelClass}>From Date</label>
                <input
                  className={inputClass}
                  placeholder="dd/MM/yyyy"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                />
              </div>

              <div>
                <label className={labelClass}>To Date</label>
                <input
                  className={inputClass}
                  placeholder="dd/MM/yyyy"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                />
              </div>
            </div>
          </div>

          <div
            className={`rounded-xl shadow-sm border overflow-hidden ${
              isDark ? "border-gray-700" : "border-gray-200"
            }`}
          >
            {normalized.length === 0 ? (
              <div className="text-center py-16">
                <div
                  className={`inline-flex items-center justify-center w-20 h-20 rounded-full mb-6 ${
                    isDark ? "bg-gray-700" : "bg-gray-100"
                  }`}
                >
                  <FaInfoCircle className="w-10 h-10 text-gray-400" />
                </div>
                <h3 className={`text-xl font-semibold mb-2 ${isDark ? "text-gray-200" : "text-gray-800"}`}>
                  {pageLoading ? "Loading..." : "No Records Found"}
                </h3>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className={`${buttonClass} text-white`}>
                    <tr>
                      <th className="px-4 py-3 text-center whitespace-nowrap">S.No</th>
                      <th className="px-4 py-3 text-center whitespace-nowrap">Open</th>
                      <th className="px-4 py-3 text-center whitespace-nowrap">Vehicle Code</th>
                      <th className="px-4 py-3 text-center whitespace-nowrap">Vehicle Name</th>
                      <th className="px-4 py-3 text-center whitespace-nowrap">Vehicle Regno</th>
                      <th className="px-4 py-3 text-center whitespace-nowrap">Total Working Hr</th>
                      <th className="px-4 py-3 text-center whitespace-nowrap">Total HSD Filling (Hr)</th>
                      <th className="px-4 py-3 text-center whitespace-nowrap">Total Working KM</th>
                      <th className="px-4 py-3 text-center whitespace-nowrap">Total HSD Filling (KM)</th>
                      <th className="px-4 py-3 text-center whitespace-nowrap">HSD Consump Hr</th>
                      <th className="px-4 py-3 text-center whitespace-nowrap">HSD Consump KM</th>
                      <th className="px-4 py-3 text-center whitespace-nowrap">Last Cum Hr</th>
                      <th className="px-4 py-3 text-center whitespace-nowrap">Last Cum KM</th>
                      <th className="px-4 py-3 text-center whitespace-nowrap">Last Updated Date</th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {normalized.map((r) => (
                      <tr
                        key={`${r.fld_vehicle_code}-${r.sno}`}
                        className={`${isDark ? "bg-gray-800" : "bg-white"} ${
                          isDark ? "hover:bg-gray-700" : "hover:bg-gray-50"
                        }`}
                      >
                        <td className="px-4 py-3 text-center">{r.sno}</td>

                        <td className="px-4 py-3 text-center">
                          <button
                            onClick={() => openVehicleDetail(r.fld_vehicle_code)}
                            className={`underline font-semibold ${
                              isDark
                                ? "text-blue-100 hover:text-white"
                                : "text-blue-700 hover:text-blue-900"
                            }`}
                            title="Open detail"
                          >
                            Open
                          </button>
                        </td>

                        <td className="px-4 py-3 text-center">{r.fld_vehicle_code}</td>
                        <td className="px-4 py-3">{r.fld_vehicle_name}</td>
                        <td className="px-4 py-3 text-center">{r.fld_vehicle_regno}</td>
                        <td className="px-4 py-3 text-right">{r.fld_hsdfillingWorkinhr}</td>
                        <td className="px-4 py-3 text-right">{r.fld_hsdfillinghr}</td>
                        <td className="px-4 py-3 text-right">{r.fld_hsdfillingWorkingKM}</td>
                        <td className="px-4 py-3 text-right">{r.fld_hsdfillingkm}</td>
                        <td className="px-4 py-3 text-right">{r.fld_diselconsumptionhr}</td>
                        <td className="px-4 py-3 text-right">{r.fld_diselconsumptionkm}</td>
                        <td className="px-4 py-3 text-right">{r.cumulativehrs}</td>
                        <td className="px-4 py-3 text-right">{r.cumulativekm}</td>
                        <td className="px-4 py-3 text-center">{r.fld_progressentrydate}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <ToastContainer
            position="top-right"
            autoClose={3000}
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
}